import { useEffect, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  Activity, Archive, ArrowLeft, Boxes, ChevronRight, ClipboardCheck, Database,
  Factory, FileSearch, Gauge, History, Home, MoveRight, PackageSearch, Pencil,
  Plus, Save, Search as SearchIcon, ShieldCheck, ShoppingCart, Trash2, Wrench,
} from "lucide-react";
import {
  equipmentTemplates,
  seedState,
} from "./data";
import type {
  Asset, AuditEntry, Part, PlantState, SpecValue, Vendor, VerificationState,
} from "./data";
import {
  addRemoteComponent, addRemoteLineAsset, moveRemoteAsset, refreshRemotePlantState,
  remoteDataEnabled, replaceRemoteAsset, retireRemoteAsset, saveRemoteAssetPassport,
} from "./assetMutations";

const STORAGE_KEY = "plant-equipment-intelligence-v01";

type Page =
  | { kind: "dashboard" }
  | { kind: "line"; lineId: string }
  | { kind: "asset"; assetId: string }
  | { kind: "parts" }
  | { kind: "vendors" }
  | { kind: "search" }
  | { kind: "walkdown" }
  | { kind: "audit" };

type DbSetter = Dispatch<SetStateAction<PlantState>>;
type LocalAudit = (entry: Omit<AuditEntry, "id" | "at" | "user">) => void;

function loadState(): PlantState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as PlantState : seedState;
  } catch {
    return seedState;
  }
}

function percentClass(value: number) {
  return value >= 80 ? "good" : value >= 40 ? "warn" : "bad";
}

function statusClass(value: VerificationState) {
  return value === "Verified" ? "good" : value === "Needs Research" ? "warn" : "bad";
}

export default function App() {
  const [db, setDb] = useState<PlantState>(loadState);
  const [page, setPage] = useState<Page>({ kind: "dashboard" });
  const [query, setQuery] = useState("");
  const [demoRole, setDemoRole] = useState("Maintenance Manager / Engineering");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  }, [db]);

  async function refreshRemote() {
    if (!remoteDataEnabled) return;
    const fresh = await refreshRemotePlantState();
    setDb(fresh);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  }

  const localAudit: LocalAudit = (entry) => {
    if (remoteDataEnabled) return;
    const auditEntry: AuditEntry = {
      id: crypto.randomUUID(),
      at: new Date().toISOString(),
      user: demoRole,
      ...entry,
    };
    setDb((previous) => ({ ...previous, audit: [auditEntry, ...previous.audit] }));
  };

  function updateLocalAsset(updated: Asset, detail = "Asset record updated") {
    setDb((previous) => ({
      ...previous,
      assets: previous.assets.map((asset) => asset.id === updated.id ? updated : asset),
    }));
    localAudit({ action: "Edited asset", entity: updated.tag, detail });
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark"><Factory size={21} /></div>
          <div><strong>Plant Intel</strong><span>Equipment Intelligence</span></div>
        </div>
        <nav>
          <Nav active={page.kind === "dashboard"} icon={<Home size={18} />} label="Dashboard" onClick={() => setPage({ kind: "dashboard" })} />
          <Nav active={page.kind === "search"} icon={<FileSearch size={18} />} label="Global Search" onClick={() => setPage({ kind: "search" })} />
          <Nav active={page.kind === "parts"} icon={<Boxes size={18} />} label="Parts / BOM" onClick={() => setPage({ kind: "parts" })} />
          <Nav active={page.kind === "vendors"} icon={<ShoppingCart size={18} />} label="Vendors" onClick={() => setPage({ kind: "vendors" })} />
          <Nav active={page.kind === "walkdown"} icon={<ClipboardCheck size={18} />} label="Walkdown" onClick={() => setPage({ kind: "walkdown" })} />
          <Nav active={page.kind === "audit"} icon={<History size={18} />} label="Audit Log" onClick={() => setPage({ kind: "audit" })} />
        </nav>
        <div className="sidebar-bottom">
          <div className="role-label">{remoteDataEnabled ? "Permissions" : "Demo role"}</div>
          {remoteDataEnabled ? (
            <div className="data-mode"><ShieldCheck size={14} /> Supabase RLS enforced</div>
          ) : (
            <>
              <select value={demoRole} onChange={(event) => setDemoRole(event.target.value)}>
                <option>Viewer</option><option>Technician</option><option>Supervisor</option>
                <option>Maintenance Manager / Engineering</option><option>Administrator</option>
              </select>
              <div className="data-mode"><Database size={14} /> Local pilot data</div>
            </>
          )}
        </div>
      </aside>

      <main>
        <header className="topbar">
          <div className="mobile-brand"><Factory size={20} /><strong>Plant Intel</strong></div>
          <div className="global-search">
            <SearchIcon size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && query.trim()) setPage({ kind: "search" });
              }}
              placeholder='Search "6208 bearing", "CPVC extruder", "SICA"...'
            />
          </div>
          <div className="topbar-status"><ShieldCheck size={16} /> {remoteDataEnabled ? "Secure DB / Phase 3" : "Pilot / Demo"}</div>
        </header>

        <section className="content">
          {page.kind === "dashboard" && <Dashboard db={db} setPage={setPage} />}
          {page.kind === "line" && <LinePage db={db} lineId={page.lineId} setPage={setPage} setDb={setDb} localAudit={localAudit} refreshRemote={refreshRemote} />}
          {page.kind === "asset" && <AssetPage db={db} assetId={page.assetId} setPage={setPage} setDb={setDb} localAudit={localAudit} updateLocalAsset={updateLocalAsset} refreshRemote={refreshRemote} />}
          {page.kind === "parts" && <PartsPage db={db} setPage={setPage} />}
          {page.kind === "vendors" && <VendorsPage db={db} />}
          {page.kind === "search" && <SearchPage db={db} query={query} setQuery={setQuery} setPage={setPage} />}
          {page.kind === "walkdown" && <WalkdownPage db={db} setPage={setPage} />}
          {page.kind === "audit" && <AuditPage db={db} />}
        </section>
      </main>

      <div className="mobile-nav">
        <Nav active={page.kind === "dashboard"} icon={<Home size={19} />} label="Home" onClick={() => setPage({ kind: "dashboard" })} />
        <Nav active={page.kind === "search"} icon={<SearchIcon size={19} />} label="Search" onClick={() => setPage({ kind: "search" })} />
        <Nav active={page.kind === "walkdown"} icon={<ClipboardCheck size={19} />} label="Walkdown" onClick={() => setPage({ kind: "walkdown" })} />
        <Nav active={page.kind === "parts"} icon={<Boxes size={19} />} label="Parts" onClick={() => setPage({ kind: "parts" })} />
      </div>
    </div>
  );
}

function Nav({ active, icon, label, onClick }: { active: boolean; icon: ReactNode; label: string; onClick: () => void }) {
  return <button className={`nav-btn ${active ? "active" : ""}`} onClick={onClick}>{icon}<span>{label}</span></button>;
}

function Dashboard({ db, setPage }: { db: PlantState; setPage: (page: Page) => void }) {
  const current = db.assets.filter((asset) => asset.status !== "Retired");
  const needsResearch = current.filter((asset) => asset.verificationState === "Needs Research").length;
  const verified = current.filter((asset) => asset.verificationState === "Verified").length;
  const incomplete = current.filter((asset) => asset.verificationState === "Incomplete").length;

  return <>
    <Title eye="PLANT EQUIPMENT INTELLIGENCE" title="Plant Dashboard" sub="Technical source of truth for installed equipment, capabilities, parts, and configuration history." />
    <div className="metric-grid">
      <Metric icon={<Wrench />} label="Current Assets / Components" value={String(current.length)} hint={`${db.assets.length - current.length} retired history`} />
      <Metric icon={<PackageSearch />} label="Parts" value={String(db.parts.length)} hint="BOM-linked records" />
      <Metric icon={<Activity />} label="Needs Research" value={String(needsResearch)} hint="Technical gaps" />
      <Metric icon={<ShieldCheck />} label="Verified" value={String(verified)} hint={`${incomplete} incomplete`} />
    </div>
    <Section title="Extrusion Lines" sub="Actual plant lines: 1, 2, 4, 6, 7, 9, 11, 12, 14, 15, 16." />
    <div className="line-grid">
      {db.lines.map((line) => {
        const currentCount = current.filter((asset) => asset.lineId === line.id && !asset.parentId).length;
        return <button className="line-card" key={line.id} onClick={() => setPage({ kind: "line", lineId: line.id })}>
          <div className="line-card-top"><div><span className="eyebrow">{currentCount ? "ACTIVE RECORDS" : "AWAITING WALKDOWN"}</span><h3>{line.name}</h3></div><ChevronRight size={19} /></div>
          <p>{line.description}</p>
          <div className="progress-row"><span>Documentation</span><strong>{line.completeness}%</strong></div>
          <div className="progress"><span className={percentClass(line.completeness)} style={{ width: `${line.completeness}%` }} /></div>
          <div className="line-foot">{currentCount} primary equipment nodes</div>
        </button>;
      })}
    </div>
    <Section title="Recent Changes" sub="Every significant edit leaves a trail." />
    <div className="panel audit-mini">
      {db.audit.length ? db.audit.slice(0, 5).map((entry) => <AuditRow key={entry.id} entry={entry} />) : <p className="muted">No changes recorded yet.</p>}
    </div>
  </>;
}

function Metric({ icon, label, value, hint }: { icon: ReactNode; label: string; value: string; hint: string }) {
  return <div className="metric"><div className="metric-icon">{icon}</div><div><span>{label}</span><strong>{value}</strong><small>{hint}</small></div></div>;
}

function LinePage({ db, lineId, setPage, setDb, localAudit, refreshRemote }: {
  db: PlantState;
  lineId: string;
  setPage: (page: Page) => void;
  setDb: DbSetter;
  localAudit: LocalAudit;
  refreshRemote: () => Promise<void>;
}) {
  const foundLine = db.lines.find((line) => line.id === lineId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  if (!foundLine) return <div className="empty-state"><h3>Line not found</h3></div>;
  const lineRecord = foundLine;
  const primary = db.assets
    .filter((asset) => asset.lineId === lineId && !asset.parentId && asset.status !== "Retired")
    .sort((a, b) => a.order - b.order);

  async function addEquipment() {
    const lineNumber = lineRecord.name.replace(/\D/g, "").padStart(2, "0");
    const tag = window.prompt(`Asset tag for new equipment on ${lineRecord.name}:`, `L${lineNumber}-`);
    if (!tag) return;
    const name = window.prompt("Equipment name:", "Extruder");
    if (!name) return;
    const type = window.prompt("Equipment type:", name) || "Generic";
    const manufacturer = window.prompt("Manufacturer (Unknown is acceptable):", "Unknown") || "Unknown";
    const model = window.prompt("Model:", "Unknown") || "Unknown";
    const serial = window.prompt("Serial number:", "Unknown") || "Unknown";

    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        const id = await addRemoteLineAsset(lineId, { tag: tag.trim(), name: name.trim(), type: type.trim(), manufacturer, model, serial });
        await refreshRemote();
        setPage({ kind: "asset", assetId: id });
      } else {
        const id = crypto.randomUUID();
        const next: Asset = {
          id, tag: tag.trim(), lineId, order: primary.length, name: name.trim(), type: type.trim(),
          manufacturer, model, serial, status: "In Service", criticality: "B",
          verificationState: "Incomplete", completeness: 0, specs: [], capabilities: [], bom: [],
        };
        setDb((previous) => ({ ...previous, assets: [...previous.assets, next] }));
        localAudit({ action: "Added line equipment", entity: next.tag, detail: `Created ${next.name} on ${lineRecord.name}` });
        setPage({ kind: "asset", assetId: id });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to add equipment");
    } finally {
      setBusy(false);
    }
  }

  return <>
    <Back onClick={() => setPage({ kind: "dashboard" })} label="Plant Dashboard" />
    <div className="section-head inline line-page-heading">
      <Title eye="LINE DETAIL" title={lineRecord.name} sub={lineRecord.description} />
      <button className="primary" disabled={busy} onClick={() => void addEquipment()}><Plus size={16} /> Add Equipment</button>
    </div>
    {message && <div className="action-message bad-message">{message}</div>}
    {!primary.length ? (
      <div className="empty-state"><ClipboardCheck size={38} /><h3>Ready for plant walkdown</h3><p>No equipment assumptions are copied from another line.</p><button className="primary" disabled={busy} onClick={() => void addEquipment()}>Add First Equipment</button></div>
    ) : <>
      <div className="process-panel"><div className="process-heading"><div><span className="eyebrow">2D PROCESS MAP</span><h3>Current installed equipment</h3></div><span className="seed-pill">LIVE STRUCTURE</span></div><div className="process-flow">
        {primary.map((asset, index) => <div className="flow-wrap" key={asset.id}><button className="flow-node" onClick={() => setPage({ kind: "asset", assetId: asset.id })}><span className={`status-dot ${statusClass(asset.verificationState)}`} /><strong>{asset.name}</strong><small>{asset.tag}</small><div className="mini-progress"><span className={percentClass(asset.completeness)} style={{ width: `${asset.completeness}%` }} /></div></button>{index < primary.length - 1 && <ChevronRight className="flow-arrow" size={21} />}</div>)}
      </div></div>
      <Section title="Line equipment" sub="Select an asset to open its technical passport." />
      <div className="table-card"><table><thead><tr><th>Tag</th><th>Equipment</th><th>Manufacturer / Model</th><th>Verification</th><th>Complete</th></tr></thead><tbody>
        {primary.map((asset) => <tr key={asset.id} className="click-row" onClick={() => setPage({ kind: "asset", assetId: asset.id })}><td className="mono">{asset.tag}</td><td><strong>{asset.name}</strong><small>{asset.type}</small></td><td>{asset.manufacturer} / {asset.model}</td><td><Status text={asset.verificationState} /></td><td>{asset.completeness}%</td></tr>)}
      </tbody></table></div>
    </>}
  </>;
}

function AssetPage({ db, assetId, setPage, setDb, localAudit, updateLocalAsset, refreshRemote }: {
  db: PlantState;
  assetId: string;
  setPage: (page: Page) => void;
  setDb: DbSetter;
  localAudit: LocalAudit;
  updateLocalAsset: (asset: Asset, detail?: string) => void;
  refreshRemote: () => Promise<void>;
}) {
  const foundAsset = db.assets.find((asset) => asset.id === assetId);
  const [draft, setDraft] = useState<Asset | null>(foundAsset ? structuredClone(foundAsset) : null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const current = db.assets.find((asset) => asset.id === assetId);
    if (current) setDraft(structuredClone(current));
  }, [assetId, db.assets]);

  if (!foundAsset || !draft) return <div className="empty-state"><h3>Asset not found</h3></div>;
  const assetRecord: Asset = foundAsset;
  const draftRecord: Asset = draft;
  const line = db.lines.find((item) => item.id === assetRecord.lineId);
  const children = db.assets.filter((item) => item.parentId === assetRecord.id && item.status !== "Retired").sort((a, b) => a.order - b.order);
  const retired = assetRecord.status === "Retired";
  const expectedFields = equipmentTemplates[assetRecord.type] ?? equipmentTemplates.Generic;

  async function savePassport() {
    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        await saveRemoteAssetPassport(draftRecord);
        await refreshRemote();
      } else {
        updateLocalAsset(draftRecord, "Technical passport edited from application UI");
      }
      setEditing(false);
      setMessage("Saved successfully.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save asset");
    } finally {
      setBusy(false);
    }
  }

  async function addComponent() {
    const tag = window.prompt("Component asset tag:", `${assetRecord.tag}-`);
    if (!tag) return;
    const name = window.prompt("Component name:", "New Component");
    if (!name) return;
    const type = window.prompt("Equipment / component type:", "Generic") || "Generic";
    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        const id = await addRemoteComponent(assetRecord.id, { tag: tag.trim(), name: name.trim(), type });
        await refreshRemote();
        setPage({ kind: "asset", assetId: id });
      } else {
        const id = crypto.randomUUID();
        const next: Asset = {
          id, tag: tag.trim(), lineId: assetRecord.lineId, parentId: assetRecord.id,
          order: children.length, name: name.trim(), type, manufacturer: "Unknown", model: "Unknown",
          serial: "Unknown", status: "In Service", criticality: "B", verificationState: "Incomplete",
          completeness: 0, specs: [], capabilities: [], bom: [],
        };
        setDb((previous) => ({ ...previous, assets: [...previous.assets, next] }));
        localAudit({ action: "Added component", entity: assetRecord.tag, detail: `Created ${next.tag} under ${assetRecord.name}` });
        setPage({ kind: "asset", assetId: id });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to add component");
    } finally {
      setBusy(false);
    }
  }

  async function replaceAsset() {
    const serial = window.prompt("Replacement serial number (Unknown is allowed):", "Unknown");
    if (serial === null) return;
    const manufacturer = window.prompt("Replacement manufacturer:", assetRecord.manufacturer) || assetRecord.manufacturer;
    const model = window.prompt("Replacement model:", assetRecord.model) || assetRecord.model;
    const reason = window.prompt("Reason for replacement:", "Installed replacement");
    if (!reason) return;
    const guideTiWorkOrder = window.prompt("Related Guide Ti work order (optional):", "") || "";
    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        const newId = await replaceRemoteAsset(assetRecord.id, { serial, manufacturer, model, reason, guideTiWorkOrder });
        await refreshRemote();
        setPage({ kind: "asset", assetId: newId });
      } else {
        const newId = crypto.randomUUID();
        const replacement: Asset = { ...structuredClone(assetRecord), id: newId, serial, manufacturer, model, verificationState: "Incomplete", completeness: 0, status: "In Service", notes: reason };
        const historical: Asset = { ...assetRecord, status: "Retired", notes: `Replaced: ${reason}` };
        setDb((previous) => ({ ...previous, assets: previous.assets.map((item) => item.id === assetRecord.id ? historical : item).concat(replacement) }));
        localAudit({ action: "Replaced component", entity: assetRecord.tag, detail: reason });
        setPage({ kind: "asset", assetId: newId });
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to replace asset");
    } finally {
      setBusy(false);
    }
  }

  async function retireAsset() {
    const reason = window.prompt("Reason for retirement / removal:", "Removed from service");
    if (!reason) return;
    const guideTiWorkOrder = window.prompt("Related Guide Ti work order (optional):", "") || "";
    if (!window.confirm(`Retire ${assetRecord.tag}? History will be preserved.`)) return;
    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        await retireRemoteAsset(assetRecord.id, reason, guideTiWorkOrder);
        await refreshRemote();
      } else {
        setDb((previous) => ({ ...previous, assets: previous.assets.map((item) => item.id === assetRecord.id ? { ...item, status: "Retired", notes: reason } : item) }));
        localAudit({ action: "Retired asset", entity: assetRecord.tag, detail: reason });
      }
      setPage({ kind: "line", lineId: assetRecord.lineId });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to retire asset");
    } finally {
      setBusy(false);
    }
  }

  async function moveAsset() {
    const targetText = window.prompt(`Move ${assetRecord.tag} to which line?`, line?.name.replace("Line ", "") || "");
    if (!targetText) return;
    const targetLine = db.lines.find((item) => item.name.toLowerCase() === `line ${targetText.trim()}`.toLowerCase() || item.name.toLowerCase() === targetText.trim().toLowerCase());
    if (!targetLine) { setMessage(`Line ${targetText} was not found.`); return; }
    const parentTag = window.prompt("Optional target parent asset tag. Leave blank for primary line equipment:", "") || "";
    const targetParent = parentTag ? db.assets.find((item) => item.lineId === targetLine.id && item.tag.toLowerCase() === parentTag.toLowerCase() && item.status !== "Retired") : undefined;
    if (parentTag && !targetParent) { setMessage(`Target parent ${parentTag} was not found on ${targetLine.name}.`); return; }
    const reason = window.prompt("Reason for move:", `Moved to ${targetLine.name}`);
    if (!reason) return;
    setBusy(true);
    setMessage("");
    try {
      if (remoteDataEnabled) {
        await moveRemoteAsset(assetRecord.id, targetLine.id, targetParent?.id ?? null, reason);
        await refreshRemote();
      } else {
        setDb((previous) => ({ ...previous, assets: previous.assets.map((item) => item.id === assetRecord.id ? { ...item, lineId: targetLine.id, parentId: targetParent?.id, order: 0 } : item) }));
        localAudit({ action: "Moved asset", entity: assetRecord.tag, detail: reason });
      }
      setPage({ kind: "asset", assetId: assetRecord.id });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to move asset");
    } finally {
      setBusy(false);
    }
  }

  return <>
    <Back onClick={() => setPage({ kind: "line", lineId: assetRecord.lineId })} label={line?.name || "Line"} />
    {message && <div className={`action-message ${message.includes("successfully") ? "good-message" : "bad-message"}`}>{message}</div>}
    <div className="passport-head">
      <div><div className="eyebrow">ASSET TECHNICAL PASSPORT</div><h1>{assetRecord.name}</h1><div className="asset-meta"><span className="mono">{assetRecord.tag}</span><span>{assetRecord.type}</span><Status text={assetRecord.verificationState} /><span>Criticality {assetRecord.criticality}</span>{retired && <span className="retired-pill">HISTORICAL / RETIRED</span>}</div></div>
      <div className="head-actions">{!retired && <>
        <button className="secondary" disabled={busy} onClick={() => void addComponent()}><Plus size={16} /> Add Component</button>
        <button className="secondary" disabled={busy} onClick={() => void moveAsset()}><MoveRight size={16} /> Move</button>
        <button className="secondary" disabled={busy} onClick={() => void replaceAsset()}><Archive size={16} /> Replace</button>
        <button className="secondary danger" disabled={busy} onClick={() => void retireAsset()}><Trash2 size={16} /> Retire</button>
        <button className={editing ? "primary" : "secondary"} disabled={busy} onClick={() => editing ? void savePassport() : setEditing(true)}>{editing ? <Save size={16} /> : <Pencil size={16} />} {editing ? "Save" : "Edit"}</button>
      </>}</div>
    </div>
    <div className="passport-kpis"><Kpi label="Manufacturer" value={assetRecord.manufacturer} /><Kpi label="Model" value={assetRecord.model} /><Kpi label="Serial" value={assetRecord.serial} /><Kpi label="Documentation" value={`${assetRecord.completeness}%`} /><Kpi label="Guide Ti" value={assetRecord.guideTiId || "Not linked"} /></div>
    <div className="tabs">{["overview", "configuration", "specifications", "capabilities", "bom", "documents", "measurements", "history", "guide ti"].map((name) => <button key={name} className={tab === name ? "active" : ""} onClick={() => setTab(name)}>{name}</button>)}</div>

    {tab === "overview" && <div className="two-col"><div className="panel"><Section title="Identity" sub="What is physically installed right now." compact />
      <Field label="Equipment name" value={draftRecord.name} editing={editing} onChange={(value) => setDraft({ ...draftRecord, name: value })} />
      <Field label="Manufacturer" value={draftRecord.manufacturer} editing={editing} onChange={(value) => setDraft({ ...draftRecord, manufacturer: value })} />
      <Field label="Model" value={draftRecord.model} editing={editing} onChange={(value) => setDraft({ ...draftRecord, model: value })} />
      <Field label="Serial" value={draftRecord.serial} editing={editing} onChange={(value) => setDraft({ ...draftRecord, serial: value })} />
      <Field label="Guide Ti Asset ID" value={draftRecord.guideTiId || ""} editing={editing} onChange={(value) => setDraft({ ...draftRecord, guideTiId: value })} />
      <SelectField label="Status" value={draftRecord.status} editing={editing} options={["In Service", "Out of Service", "Spare"]} onChange={(value) => setDraft({ ...draftRecord, status: value as Asset["status"] })} />
      <SelectField label="Criticality" value={draftRecord.criticality} editing={editing} options={["A", "B", "C"]} onChange={(value) => setDraft({ ...draftRecord, criticality: value as Asset["criticality"] })} />
      <SelectField label="Verification" value={draftRecord.verificationState} editing={editing} options={["Verified", "Needs Research", "Incomplete"]} onChange={(value) => setDraft({ ...draftRecord, verificationState: value as Asset["verificationState"] })} />
      <NumberField label="Completeness %" value={draftRecord.completeness} editing={editing} onChange={(value) => setDraft({ ...draftRecord, completeness: value })} />
    </div><div className="panel"><Section title="Data quality" sub="Unknown is acceptable. Unverified guesses are not." compact /><div className="quality-card"><Gauge /><div><strong>{assetRecord.completeness}% complete</strong><p>Use plant walkdown, nameplates, drawings, and OEM manuals to close technical gaps.</p></div></div><div className="template-list"><span>Expected {assetRecord.type} fields</span>{expectedFields.slice(0, 12).map((field) => <small key={field}>{field}</small>)}</div></div></div>}
    {tab === "configuration" && <div className="panel"><Section title="Installed configuration" sub="Current child assemblies and components." compact />{children.length ? <div className="component-grid">{children.map((child) => <button className="component-card" key={child.id} onClick={() => setPage({ kind: "asset", assetId: child.id })}><div><Status text={child.verificationState} /><h3>{child.name}</h3><span className="mono">{child.tag}</span></div><ChevronRight /></button>)}</div> : <p className="muted">No current child components documented yet.</p>}</div>}
    {tab === "specifications" && <Specs draft={draftRecord} setDraft={setDraft} editing={editing} />}
    {tab === "capabilities" && <Caps draft={draftRecord} setDraft={setDraft} editing={editing} />}
    {tab === "bom" && <Bom db={db} asset={assetRecord} setPage={setPage} />}
    {tab === "documents" && <Placeholder title="Documents & Photos" text="Private storage is provisioned. Uploads are the next Phase 3 checkpoint." />}
    {tab === "measurements" && <Placeholder title="Inspection / Measurements" text="Screw wear, barrel bore measurements, vibration, alignment, backlash, clearances and other inspections." />}
    {tab === "history" && <HistoryView db={db} asset={assetRecord} />}
    {tab === "guide ti" && <Placeholder title="Guide Ti Link" text={`Guide Ti reference: ${assetRecord.guideTiId || "Not yet linked"}. Guide Ti remains the CMMS for PMs and work orders.`} />}
  </>;
}

function Kpi({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function Specs({ draft, setDraft, editing }: { draft: Asset; setDraft: Dispatch<SetStateAction<Asset | null>>; editing: boolean }) {
  function change(index: number, patch: Partial<SpecValue>) {
    setDraft({ ...draft, specs: draft.specs.map((spec, current) => current === index ? { ...spec, ...patch } : spec) });
  }
  return <div className="panel"><div className="section-head inline"><div><h2>Technical specifications</h2><p>Every value carries specification class and verification source.</p></div>{editing && <button className="secondary" onClick={() => setDraft({ ...draft, specs: [...draft.specs, { label: "New specification", value: "Unknown", class: "AS-BUILT / INSTALLED", verification: "Unknown" }] })}><Plus size={15} /> Add spec</button>}</div><div className="spec-table"><div className="spec-row spec-head"><span>Specification</span><span>Value / Unit</span><span>Class</span><span>Verified from</span></div>
    {draft.specs.map((spec, index) => <div className="spec-row" key={`${spec.label}-${index}`}>
      {editing ? <div className="inline-edit"><input value={spec.label} onChange={(event) => change(index, { label: event.target.value })} /><button className="icon-btn" onClick={() => setDraft({ ...draft, specs: draft.specs.filter((_, current) => current !== index) })}><Trash2 size={13} /></button></div> : <strong>{spec.label}</strong>}
      {editing ? <div className="value-unit"><input value={spec.value} onChange={(event) => change(index, { value: event.target.value })} /><input value={spec.unit || ""} placeholder="unit" onChange={(event) => change(index, { unit: event.target.value })} /></div> : <span>{spec.value}{spec.unit ? ` ${spec.unit}` : ""}</span>}
      {editing ? <select value={spec.class} onChange={(event) => change(index, { class: event.target.value as SpecValue["class"] })}><option>OEM SPEC</option><option>AS-BUILT / INSTALLED</option><option>PLANT VALIDATED</option></select> : <span className="spec-class">{spec.class}</span>}
      {editing ? <select value={spec.verification} onChange={(event) => change(index, { verification: event.target.value as SpecValue["verification"] })}><option>Unknown</option><option>OEM manual</option><option>Nameplate</option><option>Drawing</option><option>Measured in field</option><option>Plant estimate</option></select> : <span>{spec.verification}</span>}
    </div>)}
    {!draft.specs.length && <p className="muted">No specifications documented yet.</p>}
  </div></div>;
}

function Caps({ draft, setDraft, editing }: { draft: Asset; setDraft: Dispatch<SetStateAction<Asset | null>>; editing: boolean }) {
  return <div className="panel"><div className="section-head inline"><div><h2>Capability</h2><p>Record what the machine is validated to do.</p></div>{editing && <button className="secondary" onClick={() => setDraft({ ...draft, capabilities: [...draft.capabilities, { label: "New capability", value: "Unknown" }] })}><Plus size={15} /> Add capability</button>}</div><div className="cap-grid">
    {draft.capabilities.map((capability, index) => <div className="cap-card" key={index}>{editing ? <><input value={capability.label} onChange={(event) => { const next = [...draft.capabilities]; next[index] = { ...next[index], label: event.target.value }; setDraft({ ...draft, capabilities: next }); }} /><div className="inline-edit"><input value={capability.value} onChange={(event) => { const next = [...draft.capabilities]; next[index] = { ...next[index], value: event.target.value }; setDraft({ ...draft, capabilities: next }); }} /><button className="icon-btn" onClick={() => setDraft({ ...draft, capabilities: draft.capabilities.filter((_, current) => current !== index) })}><Trash2 size={13} /></button></div></> : <><span>{capability.label}</span><strong>{capability.value}</strong></>}</div>)}
  </div></div>;
}

function Bom({ db, asset, setPage }: { db: PlantState; asset: Asset; setPage: (page: Page) => void }) {
  const rows = asset.bom.map((item) => ({ item, part: db.parts.find((part) => part.id === item.partId) })).filter((row) => row.part);
  return <div className="panel"><div className="section-head inline"><div><h2>BOM / Parts</h2><p>Exact service parts associated with this asset.</p></div><button className="secondary" onClick={() => setPage({ kind: "parts" })}>Open Parts Database</button></div>{rows.length ? <table><thead><tr><th>Part</th><th>OEM PN</th><th>Qty</th><th>Stock</th></tr></thead><tbody>{rows.map(({ item, part }) => <tr key={item.partId}><td>{part!.description}</td><td>{part!.oemPn}</td><td>{item.qty}</td><td>{part!.stockQty}</td></tr>)}</tbody></table> : <p className="muted">No BOM items linked yet. BOM writes are the next checkpoint.</p>}</div>;
}

function HistoryView({ db, asset }: { db: PlantState; asset: Asset }) {
  const rows = db.audit.filter((entry) => entry.entity === asset.id || entry.entity === asset.tag || entry.detail.includes(asset.tag));
  return <div className="panel"><Section title="Configuration / Audit History" sub="Retirements, replacements, moves and edits remain traceable." compact />{rows.length ? rows.map((entry) => <AuditRow key={entry.id} entry={entry} />) : <p className="muted">No asset-specific entries loaded here yet. Use Global Audit Log for database activity.</p>}</div>;
}

function PartsPage({ db, setPage }: { db: PlantState; setPage: (page: Page) => void }) {
  const [filter, setFilter] = useState("");
  const rows = db.parts.filter((part) => JSON.stringify(part).toLowerCase().includes(filter.toLowerCase()));
  return <><Title eye="PARTS INTELLIGENCE" title="Parts / BOM" sub="Replacement parts, approved alternates, suppliers, and plant-wide Where Used." /><div className="toolbar"><div className="search-box"><SearchIcon size={16} /><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search internal PN, OEM PN, manufacturer..." /></div><button className="primary" disabled>{remoteDataEnabled ? "Part writes next checkpoint" : "Add Part"}</button></div><div className="table-card"><table><thead><tr><th>Internal PN</th><th>OEM PN</th><th>Description</th><th>Manufacturer</th><th>Criticality</th><th>Stock</th><th>Where Used</th></tr></thead><tbody>{rows.map((part) => {
    const used = db.assets.filter((asset) => asset.bom.some((item) => item.partId === part.id));
    return <tr key={part.id}><td className="mono">{part.internalPn}</td><td className="mono">{part.oemPn}</td><td>{part.description}</td><td>{part.manufacturer}</td><td>{part.criticality}</td><td>{part.stockQty} / min {part.minQty}</td><td>{used.map((asset) => <button key={asset.id} className="text-link" onClick={() => setPage({ kind: "asset", assetId: asset.id })}>{asset.tag}</button>)}</td></tr>;
  })}</tbody></table></div></>;
}

function VendorsPage({ db }: { db: PlantState }) {
  return <><Title eye="SUPPLIER INTELLIGENCE" title="Vendors" sub="Approved and candidate sources for equipment and replacement parts." /><div className="vendor-grid">{db.vendors.length ? db.vendors.map((vendor: Vendor) => <div className="vendor-card" key={vendor.id}><h3>{vendor.name}</h3><p>{vendor.notes || "No notes"}</p><dl><dt>Website</dt><dd>{vendor.website || "—"}</dd><dt>Contact</dt><dd>{vendor.contact || "—"}</dd><dt>Phone</dt><dd>{vendor.phone || "—"}</dd><dt>Email</dt><dd>{vendor.email || "—"}</dd></dl></div>) : <div className="empty-state">No vendors documented yet.</div>}</div></>;
}

function SearchPage({ db, query, setQuery, setPage }: { db: PlantState; query: string; setQuery: (value: string) => void; setPage: (page: Page) => void }) {
  const search = query.trim().toLowerCase();
  const assets = search ? db.assets.filter((asset) => JSON.stringify(asset).toLowerCase().includes(search)) : [];
  const parts = search ? db.parts.filter((part: Part) => JSON.stringify(part).toLowerCase().includes(search)) : [];
  return <><Title eye="GLOBAL SEARCH" title="Technical Search" sub="Search tags, equipment, manufacturer, model, serial, Guide Ti IDs, parts and specification text." /><div className="big-search"><SearchIcon /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} /></div><Section title={`Results${search ? ` for “${query}”` : ""}`} sub={`${assets.length} assets/components · ${parts.length} parts`} /><div className="result-list">{assets.map((asset) => <button className="result-row" key={asset.id} onClick={() => setPage({ kind: "asset", assetId: asset.id })}><div><Status text={asset.verificationState} /><strong>{asset.name}</strong><span className="mono">{asset.tag}</span></div><span>{asset.manufacturer} · {asset.model} · {asset.status}</span><ChevronRight /></button>)}</div></>;
}

function WalkdownPage({ db, setPage }: { db: PlantState; setPage: (page: Page) => void }) {
  const [lineId, setLineId] = useState(db.lines[0]?.id || "");
  const assets = db.assets.filter((asset) => asset.lineId === lineId && asset.status !== "Retired");
  return <><Title eye="MOBILE WALKDOWN" title="Plant Walkdown" sub="Capture what is actually installed and flag technical gaps." /><div className="walkdown-shell"><div className="walkdown-top"><select value={lineId} onChange={(event) => setLineId(event.target.value)}>{db.lines.map((line) => <option key={line.id} value={line.id}>{line.name}</option>)}</select><span>{assets.length} current records</span></div>{assets.length ? <div className="component-grid">{assets.map((asset) => <button key={asset.id} className="component-card" onClick={() => setPage({ kind: "asset", assetId: asset.id })}><div><Status text={asset.verificationState} /><h3>{asset.name}</h3><span className="mono">{asset.tag}</span></div><ChevronRight /></button>)}</div> : <div className="empty-state"><p>No equipment records on this line yet.</p></div>}</div></>;
}

function AuditPage({ db }: { db: PlantState }) {
  return <><Title eye="TRACEABILITY" title="Audit Log" sub="Who changed what, when, and why." /><div className="panel audit-mini">{db.audit.length ? db.audit.map((entry) => <AuditRow key={entry.id} entry={entry} />) : <p className="muted">No audit records yet.</p>}</div></>;
}

function Field({ label, value, editing, onChange }: { label: string; value: string; editing: boolean; onChange: (value: string) => void }) {
  return <div className="field"><span>{label}</span>{editing ? <input value={value} onChange={(event) => onChange(event.target.value)} /> : <strong>{value || "Unknown"}</strong>}</div>;
}
function SelectField({ label, value, editing, options, onChange }: { label: string; value: string; editing: boolean; options: string[]; onChange: (value: string) => void }) {
  return <div className="field"><span>{label}</span>{editing ? <select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option}>{option}</option>)}</select> : <strong>{value}</strong>}</div>;
}
function NumberField({ label, value, editing, onChange }: { label: string; value: number; editing: boolean; onChange: (value: number) => void }) {
  return <div className="field"><span>{label}</span>{editing ? <input type="number" min={0} max={100} value={value} onChange={(event) => onChange(Math.max(0, Math.min(100, Number(event.target.value))))} /> : <strong>{value}</strong>}</div>;
}
function Status({ text }: { text: VerificationState }) { return <span className={`status ${statusClass(text)}`}>{text}</span>; }
function Back({ onClick, label }: { onClick: () => void; label: string }) { return <button className="back-btn" onClick={onClick}><ArrowLeft size={15} />{label}</button>; }
function Title({ eye, title, sub }: { eye: string; title: string; sub: string }) { return <div className="page-title"><div className="eyebrow">{eye}</div><h1>{title}</h1><p>{sub}</p></div>; }
function Section({ title, sub, compact = false }: { title: string; sub: string; compact?: boolean }) { return <div className={`section-head ${compact ? "compact" : ""}`}><h2>{title}</h2><p>{sub}</p></div>; }
function AuditRow({ entry }: { entry: AuditEntry }) { return <div className="audit-row"><div className="audit-icon"><History size={14} /></div><div><strong>{entry.action}</strong><span>{entry.entity} · {entry.detail}</span></div><time>{new Date(entry.at).toLocaleString()}</time></div>; }
function Placeholder({ title, text }: { title: string; text: string }) { return <div className="panel placeholder"><ClipboardCheck size={28} /><h3>{title}</h3><p>{text}</p></div>; }
